import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Eye, RefreshCw, CheckCircle2, XCircle, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const KIND_LABELS = {
  generic: "Générique",
  test: "Test SMTP",
  contact_form: "Contact → admin",
  contact_confirmation: "Confirmation contact",
  chat_handover: "Chat → admin",
  chat_confirmation: "Confirmation chat",
  new_application: "Candidature → admin",
  application_received: "Confirmation candidature",
  application_decision: "Décision candidature",
  new_lead: "Lead programme → admin",
  programme_pdf_to_prospect: "Programme PDF → prospect",
  commission_validated: "Commission validée",
  quote_admin: "Devis → admin",
  quote_client: "Confirmation devis",
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "—";
  }
};

export const EmailHistoryAdmin = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/smtp/logs?limit=200`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setLogs(await res.json());
    } catch {
      toast.error("Impossible de charger l'historique des emails");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openPreview = async (log) => {
    setLoadingPreview(true);
    try {
      const res = await fetch(`${API}/api/admin/smtp/logs/${log.id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setPreview(await res.json());
    } catch {
      toast.error("Aperçu indisponible pour cet email");
    } finally {
      setLoadingPreview(false);
    }
  };

  const filtered = logs.filter((l) => {
    const q = query.toLowerCase();
    return !q || (l.to || "").toLowerCase().includes(q) || (l.subject || "").toLowerCase().includes(q) || (KIND_LABELS[l.kind] || l.kind || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-5" data-testid="email-history-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-medium text-[#1F2A4A] flex items-center gap-2"><Mail className="w-5 h-5 text-[#C9B896]" /> Historique des emails</h2>
          <p className="text-sm text-[#1F2A4A]/60">Tous les emails envoyés depuis le site — {logs.length} au total</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1F2A4A]/40" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher (destinataire, sujet…)" className="pl-9 w-64" data-testid="email-history-search" />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="email-history-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#1F2A4A]/60 py-10 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[#1F2A4A]/60 py-10 text-center" data-testid="email-history-empty">Aucun email pour l'instant.</p>
      ) : (
        <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F3EDE0] text-[#1F2A4A] text-left">
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Date & heure</th>
                  <th className="px-4 py-3 font-medium">Destinataire</th>
                  <th className="px-4 py-3 font-medium">Sujet</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Type</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium text-right">Aperçu</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t border-[#1F2A4A]/8 hover:bg-[#F3EDE0]/40" data-testid={`email-log-row-${l.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-[#1F2A4A]/80">{fmt(l.sent_at)}</td>
                    <td className="px-4 py-3 text-[#1F2A4A] break-all">{l.to}</td>
                    <td className="px-4 py-3 text-[#1F2A4A]/90 max-w-[280px] truncate" title={l.subject}>{l.subject}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant="outline" className="border-[#C9B896] text-[#8a7340] text-[11px]">{KIND_LABELS[l.kind] || l.kind}</Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {l.ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Envoyé</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 text-xs" title={l.error}><XCircle className="w-3.5 h-3.5" /> {l.error?.startsWith("SMTP désactivé") ? "Non envoyé (SMTP off)" : "Échec"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" className="text-xs" disabled={loadingPreview} onClick={() => openPreview(l)} data-testid={`email-preview-btn-${l.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1F2A4A] text-base pr-8">{preview?.subject}</DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <div className="text-xs text-[#1F2A4A]/70 space-y-0.5 border-b border-[#1F2A4A]/10 pb-3">
                <p><strong>À :</strong> {preview.to} {preview.from_email && <> · <strong>De :</strong> {preview.from_email}</>}</p>
                <p><strong>Date :</strong> {fmt(preview.sent_at)} · <strong>Statut :</strong> {preview.ok ? "Envoyé ✓" : `Non envoyé — ${preview.error}`}</p>
              </div>
              <div className="flex-1 overflow-auto rounded-lg border border-[#1F2A4A]/10 bg-[#F3EDE0]">
                {preview.body_html ? (
                  <iframe title="Aperçu email" srcDoc={preview.body_html} sandbox="" className="w-full h-[55vh] bg-white" />
                ) : (
                  <pre className="p-4 text-xs whitespace-pre-wrap">{preview.body_text || "—"}</pre>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
